import axios from 'axios';
import { z } from 'zod';

const geocodingResponseSchema = z.object({
  results: z.array(z.object({
    components: z.object({
      city: z.string().optional(),
      town: z.string().optional(),
      village: z.string().optional(),
      country: z.string().optional(),
    }),
  })),
});

interface Location {
  city: string | null;
  country: string | null;
}

export async function getLocationFromCoordinates(
  latitude: number,
  longitude: number,
  apiKey: string
): Promise<Location> {
  const url = `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${apiKey}`;

  try {
    // Make the GET request to OpenCage API
    const response = await axios.get(url);

    // Validate the response data
    const parsed = geocodingResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error('Failed to parse geocoding response:', parsed.error);
      throw new Error('Invalid response format from geocoding service');
    }

    // Check if there are any results
    if (parsed.data.results && parsed.data.results.length > 0) {
      let city: string | null = null;
      let country: string | null = null;

      // Loop through results to find city and country
      for (const result of parsed.data.results) {
        const components = result.components;

        // Try to get city from various fields
        city = components.city || components.town || components.village || null;
        country = components.country || null;

        // Break the loop if we found both city and country
        if (city && country) {
          break;
        }
      }

      if (!city || !country) {
        throw new Error('Location not found at these coordinates');
      }

      return { city, country };
    } else {
      throw new Error('No results found for these coordinates');
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Invalid API key or unauthorized access');
      } else if (error.response?.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later');
      } else if (!error.response) {
        throw new Error('Network error. Please check your connection');
      }
    }
    
    // Re-throw the error with a more specific message if we created it
    if (error.message.includes('Location not found') || 
        error.message.includes('No results found') ||
        error.message.includes('Invalid API key') ||
        error.message.includes('Rate limit') ||
        error.message.includes('Network error')) {
      throw error;
    }
    
    // Generic error for unexpected cases
    throw new Error('Failed to get location. Please try again');
  }
}